import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, MotionConfig } from 'motion/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AudioLines, MessageSquare, PanelLeft, Plus, Sparkles } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { useAICopilotGate } from '@/hooks/useAICopilotGate'
import { useOwnerBrief } from '@/hooks/useOwnerBrief'
import { copilotCapabilities } from '@/lib/aiCapabilities'
import { promptsFor } from '@/lib/aiScreenPrompts'
import { cn, formatRelative } from '@/lib/utils'
import type { AIConversation } from '@/types/ai'
import { defaultCapabilities } from './BhooklyAI'
import { AssistantRow, CapabilityChips, Orb, PROMPT_ICONS, UserRow } from './chatRows'
import { ChatComposer } from './ChatComposer'
import { useFileDrop } from './attachmentViews'
import { useCopilotChat, type Capabilities } from './useCopilotChat'
import { VoiceMode } from './VoiceMode'

// ── Bhookly AI page (F-AI-51): /admin/ai ────────────────────────────────────
// The copilot as a place of its own: past conversations on the left, the chat
// in the middle, and voice mode as a full-screen state. Same engine as the
// bubble (useCopilotChat), same stored conversation, so a chat started in one
// continues in the other.

const CONVERSATIONS_KEY = ['ai', 'conversations'] as const

function ConversationRow({
  c,
  active,
  onOpen,
}: {
  c: AIConversation
  active: boolean
  onOpen: (id: string) => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(c.id)}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted',
          active && 'bg-muted',
        )}
      >
        <span className="line-clamp-2 text-sm text-foreground">{c.preview || 'New chat'}</span>
        <span className="text-[11px] text-muted-foreground">{formatRelative(c.last_message_at)}</span>
      </button>
    </li>
  )
}

export function BhooklyAIPage() {
  const gate = useAICopilotGate()
  const queryClient = useQueryClient()
  const [caps, setCapsState] = useState<Capabilities>(defaultCapabilities)
  const setCaps = useCallback((patch: Partial<Capabilities>) => setCapsState((c) => ({ ...c, ...patch })), [])
  const chat = useCopilotChat({
    role: gate.role,
    userId: gate.userId,
    flags: gate.flags,
    onHide: gate.hide,
    caps,
    setCaps,
  })
  const drop = useFileDrop(caps.files && !chat.reportMode, chat.addFiles)
  const {
    messages,
    send,
    seed,
    newChat,
    openConversation,
    conversationId,
    sending,
    speech,
    onFeedback,
    actOnCard,
    openCardScreen,
    pendingLabel,
    scrollRef,
  } = chat
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [listOpen, setListOpen] = useState(false)

  const list = useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => apiClient.aiListConversations(),
    enabled: gate.show,
    staleTime: 30_000,
  })
  // A turn can start a conversation or move one to the top: refresh the
  // history once the turn is over.
  useEffect(() => {
    if (!sending) void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY })
  }, [sending, queryClient])

  const ownerBrief = useOwnerBrief(gate.userId, gate.show ? gate.role : '')
  const briefChips = (ownerBrief.brief?.metrics?.suggestions ?? []).filter((s) => s && s.label && s.prompt)
  const starters = useMemo(() => promptsFor('/admin/ai', gate.role), [gate.role])
  const capabilities = useMemo(
    () => copilotCapabilities(gate.role, { flags: gate.flags, writes: gate.writes }),
    [gate.role, gate.flags, gate.writes],
  )

  const open = useCallback(
    (id: string) => {
      setListOpen(false)
      void openConversation(id)
    },
    [openConversation],
  )

  if (!gate.show) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <Orb size={48} />
        <p className="text-base font-semibold">Bhookly AI isn't switched on for this restaurant</p>
        <p className="max-w-md text-sm text-muted-foreground">
          The copilot is switched on per restaurant and per role. Ask Bhookly support to enable it.
        </p>
      </div>
    )
  }

  const empty = messages.length === 0
  const conversations = list.data?.data ?? []

  const history = (
    <nav aria-label="Past chats" className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-3">
        <Button type="button" size="sm" variant="outline" className="flex-1" onClick={newChat} disabled={sending}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden /> New chat
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {list.isLoading ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
        ) : conversations.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">No past chats yet.</p>
        ) : (
          <ul className="space-y-0.5">
            {conversations.map((c) => (
              <ConversationRow key={c.id} c={c} active={c.id === conversationId} onOpen={open} />
            ))}
          </ul>
        )}
      </div>
    </nav>
  )

  return (
    <MotionConfig reducedMotion="user">
      <div data-ai-page="" className="flex h-[calc(100dvh-3.5rem)] min-h-0 w-full md:h-dvh">
        {/* History: a column on wide screens, a drawer below md */}
        <aside className="hidden w-64 shrink-0 border-r border-border bg-background md:block">{history}</aside>
        {listOpen && (
          <div className="fixed inset-0 z-[55] flex md:hidden">
            <div className="w-72 max-w-[85vw] border-r border-border bg-background shadow-xl">{history}</div>
            <button type="button" aria-label="Close past chats" className="flex-1 bg-black/40" onClick={() => setListOpen(false)} />
          </div>
        )}

        <section className="relative flex min-w-0 flex-1 flex-col" {...drop.props}>
          <header className="flex items-center gap-3 border-b border-border px-4 py-3">
            <button
              type="button"
              onClick={() => setListOpen(true)}
              aria-label="Past chats"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
            >
              <PanelLeft className="h-5 w-5" aria-hidden />
            </button>
            <Orb size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">Bhookly AI</p>
              <p className="truncate text-xs text-muted-foreground">Your POS assistant · ask, find, get it done</p>
            </div>
            {caps.voiceIn && (
              <Button type="button" size="sm" onClick={() => setVoiceOpen(true)} disabled={sending}>
                <AudioLines className="mr-1.5 h-4 w-4" aria-hidden /> Voice
              </Button>
            )}
          </header>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto" aria-live="polite">
            <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6">
              {empty ? (
                <div className="flex flex-col items-center px-2 pt-10 text-center">
                  <Orb size={64} />
                  <h2 className="mt-5 text-xl font-semibold">How can I help?</h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">Type or speak, in English or Urdu.</p>
                  <div className="mt-6 w-full max-w-xl">
                    <CapabilityChips items={capabilities} onPick={seed} disabled={sending} />
                  </div>
                  {briefChips.length > 0 && (
                    <div className="mt-6 flex flex-wrap justify-center gap-2">
                      {briefChips.map((s) => (
                        <button
                          type="button"
                          key={s.label}
                          onClick={() => void send(s.prompt)}
                          disabled={sending}
                          className="rounded-full border border-primary/40 bg-background px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-6 grid w-full gap-2 sm:grid-cols-2">
                    {starters.map((s) => (
                      <button
                        type="button"
                        key={s.text}
                        onClick={() => (s.send ? void send(s.text) : seed(s.text))}
                        disabled={sending}
                        className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-3 text-left text-sm transition-colors hover:bg-muted disabled:opacity-50"
                      >
                        <span className="text-primary">{PROMPT_ICONS[s.icon]}</span>
                        <span className="text-foreground">{s.text}</span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-8 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                    Past chats are on the left.
                    <Sparkles className="ml-2 h-3.5 w-3.5" aria-hidden />
                    The bubble on every screen is the same assistant.
                  </p>
                </div>
              ) : (
                messages.map((m) =>
                  m.role === 'assistant' ? (
                    <AssistantRow
                      key={m.id}
                      message={m}
                      pendingLabel={m.pending ? pendingLabel : undefined}
                      canSpeak={caps.voiceOut}
                      voiceStatus={speech.voiceState?.id === m.id ? speech.voiceState.status : undefined}
                      onSpeak={speech.onSpeak}
                      onFeedback={onFeedback}
                      onCardAction={actOnCard}
                      onOpenScreen={openCardScreen}
                      onAsk={(text) => void send(text)}
                    />
                  ) : (
                    <UserRow key={m.id} message={m} />
                  ),
                )
              )}
            </div>
          </div>

          <div className="mx-auto w-full max-w-3xl">
            <ChatComposer chat={chat} caps={caps} size="md" />
            {drop.overlay}
          </div>
        </section>
      </div>

      <AnimatePresence>{voiceOpen && <VoiceMode key="voice" chat={chat} onClose={() => setVoiceOpen(false)} />}</AnimatePresence>
    </MotionConfig>
  )
}

export default BhooklyAIPage
